import type { ReactNode } from 'react';
import { Badge } from '@/shared/ui/badge';
import { formatCount } from '@/shared/format';
import { AvailabilityNotice, CapabilityBadges } from '@/modules/ai/shared';
import type { EffectiveAiSettings } from '@/api/schemas/ai';

/**
 * The tenant's RESOLVED AI configuration.
 *
 * This is what actually happens when the tenant asks for AI — not what the
 * tenant believes it configured. The five availability states each have one
 * plain sentence, and every state that is not `ready` says plainly that the
 * manual forms still work.
 */
export type EffectiveSettingsPanelProps = {
  settings: EffectiveAiSettings;
};

export function EffectiveSettingsPanel({ settings }: EffectiveSettingsPanelProps): ReactNode {
  const provider = settings.provider;

  return (
    <div className="space-y-6">
      <section className="space-y-3 rounded-lg border border-border p-6">
        <h3 className="text-lg font-medium">Availability</h3>
        <AvailabilityNotice availability={settings.availability} reason={settings.reason} />
      </section>

      <section className="space-y-3 rounded-lg border border-border p-6">
        <h3 className="text-lg font-medium">Effective configuration</h3>
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Configuration source" value={sourceLabel(settings.configurationSource)} />
          <Field
            label="Platform fallback applied"
            value={settings.platformFallbackApplied ? 'Yes' : 'No'}
          />
          <Field label="Provider" value={provider === null ? 'None' : provider.modelId} />
          <Field label="Base URL" value={provider === null ? '—' : provider.baseUrl} />
          <Field
            label="Billed to"
            value={provider === null ? '—' : billingLabel(provider.billingSource)}
          />
          <Field
            label="Timeout"
            value={provider === null ? '—' : `${provider.timeoutMs} ms`}
          />
        </dl>

        <div className="pt-2">
          <p className="mb-2 text-xs tracking-wide text-content-muted uppercase">Provider capabilities</p>
          {provider === null ? (
            <p className="text-sm text-content-muted">No provider is configured.</p>
          ) : (
            <CapabilityBadges capabilities={provider.capabilities} />
          )}
        </div>
      </section>

      <section className="space-y-3 rounded-lg border border-border p-6">
        <h3 className="text-lg font-medium">Limits</h3>
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Tenant requests" value={limitLabel(settings.limits.tenantRequestLimit)} />
          <Field label="Tenant tokens" value={limitLabel(settings.limits.tenantTokenLimit)} />
          <Field label="Platform requests" value={limitLabel(settings.limits.platformRequestLimit)} />
          <Field label="Platform tokens" value={limitLabel(settings.limits.platformTokenLimit)} />
        </dl>
      </section>

      <section className="space-y-3 rounded-lg border border-border p-6">
        <h3 className="text-lg font-medium">Current usage</h3>
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Platform requests" value={formatCount(settings.usage.platformRequestCount)} />
          <Field label="Tenant requests" value={formatCount(settings.usage.tenantOwnedRequestCount)} />
          <Field label="Platform tokens" value={usageCount(settings.usage.platformTokenCount)} />
          <Field label="Tenant tokens" value={usageCount(settings.usage.tenantOwnedTokenCount)} />
        </dl>
      </section>
    </div>
  );
}

function sourceLabel(source: EffectiveAiSettings['configurationSource']): string {
  if (source === 'platform_default') return 'Platform default';
  if (source === 'tenant_owned') return 'Tenant-owned';
  return 'None';
}

function billingLabel(source: 'platform' | 'tenant_owned'): string {
  return source === 'platform' ? 'Platform allowance' : 'Your own provider';
}

/**
 * A usage count.
 *
 * The API reports a TOKEN count as null when there is no usage row for the period, and a
 * missing count is NOT zero: zero would claim that nothing was used, which is a claim the
 * server has not made. "Not reported" says what the server actually said.
 */
function usageCount(value: number | null): string {
  return value === null ? 'Not reported' : formatCount(value);
}

function limitLabel(limit: number | null): string {
  return limit === null ? 'No limit' : formatCount(limit);
}

function Field({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <dt className="text-xs tracking-wide text-content-muted uppercase">{label}</dt>
      <dd className="text-sm text-content">{value}</dd>
    </div>
  );
}

/**
 * A small badge for the configuration source, used where a single word is
 * enough — the tab strip, and the tenant's own settings panel.
 */
export function ConfigSourceBadge({
  source,
}: {
  source: 'platform_default' | 'tenant_owned' | null;
}): ReactNode {
  if (source === null) return <Badge tone="neutral">None</Badge>;
  if (source === 'tenant_owned') return <Badge tone="info">Tenant-owned</Badge>;
  return <Badge tone="neutral">Platform default</Badge>;
}
